import type { CustomerOrderItem } from '../../../types/customer';
import { formatDate } from '../../../utils/dateFormatters';
import { formatMoney } from '../../../utils/formatting';
import { trackEvent } from '../../../services/portalTracking';
import styles from '../PortalCustomerDetailPage.module.css';
import { RECENT_ORDERS_LIMIT, selectRecentOrders } from './recentOrders';

interface RecentOrdersCardProps {
  /** The orders the profile already loaded (shared with the Orders tab). */
  orders: readonly CustomerOrderItem[];
  /** Total order count for the customer (may exceed orders.length). */
  ordersTotal: number;
  /** Open the order detail for this order id. */
  onOpenOrder: (orderId: string) => void;
  /** Switch the page to the full Orders tab. */
  onViewAll: () => void;
}

/**
 * Recent orders on the customer Overview tab (usage-graph finding C4).
 *
 * Every open from this block and every "view all" click is recorded as a
 * named portal_events action so the usage graph can tell this path apart
 * from the Orders tab.
 */
export default function RecentOrdersCard({ orders, ordersTotal, onOpenOrder, onViewAll }: RecentOrdersCardProps) {
  const recent = selectRecentOrders(orders, RECENT_ORDERS_LIMIT);

  const handleOpen = (order: CustomerOrderItem, position: number) => {
    trackEvent('action', 'customer_overview_recent_order_open', {
      order_id: order.id,
      order_status: order.status,
      sage_order_no: order.sage_order_no,
      position,
    });
    onOpenOrder(order.id);
  };

  const handleViewAll = () => {
    trackEvent('click', 'customer_overview_view_all_orders', {
      orders_total: ordersTotal,
      shown: recent.length,
    });
    onViewAll();
  };

  return (
    <div className={styles.card} data-testid="recent-orders-card">
      <div className={styles.cardTitle}>
        <span>Recent Orders</span>
        {ordersTotal > 0 && <span className={styles.cardCount}>{ordersTotal}</span>}
        {ordersTotal > 0 && (
          <button
            type="button"
            className={styles.cardTitleLink}
            onClick={handleViewAll}
            data-action="customer_overview_view_all_orders"
          >
            View all {ordersTotal} orders &rarr;
          </button>
        )}
      </div>
      {recent.length === 0 ? (
        <div className={styles.emptyState}>No orders yet</div>
      ) : (
        <table className={`${styles.dataTable} ${styles.compactTable}`}>
          <thead>
            <tr>
              <th>Status</th>
              <th>Sage #</th>
              <th>Created</th>
              <th style={{ textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((o, i) => (
              <tr
                key={o.id}
                className={styles.clickableRow}
                onClick={() => handleOpen(o, i)}
                title="View order details"
                data-action="customer_overview_recent_order_open"
                data-order-id={o.id}
              >
                <td>
                  <span className={`${styles.orderBadge} ${styles[`order_${o.status}`] || ''}`}>
                    {o.status.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className={styles.linkCell}>{o.sage_order_no || '—'}</td>
                <td>{formatDate(o.created_at)}</td>
                <td style={{ textAlign: 'right', fontWeight: 500 }}>{formatMoney(o.total || o.subtotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
